import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const services = [
  {
    icon: "ri-newspaper-line",
    title: "资讯报道",
    text: "影视、游戏、漫画与行业四条线并行更新，包含上线消息、版本更新、分镜分析与分级讨论。每篇报道都标注来源与发布时间。",
  },
  {
    icon: "ri-folder-open-line",
    title: "资源条目",
    text: "为每部作品建立结构化条目：类型、地区、年份、推荐度与题材标签，并附适看提示，读者可以在进入之前就判断是否适合自己。",
  },
  {
    icon: "ri-discuss-line",
    title: "话题讨论",
    text: "由读者发起的讨论空间，围绕作品、行业规则与观看体验展开。我们鼓励具体、克制、有信息量的表达。",
  },
];

const principles = [
  {
    index: "01",
    title: "标注先于推荐",
    text: "任何推荐都必须先说清楚它是什么、包含什么。题材强度、呈现方式与争议情节，能标注的尽量标注。",
  },
  {
    index: "02",
    title: "克制胜过猎奇",
    text: "我们更愿意用准确的语言描述作品，而不是用夸张的标题换取点击。信息的可信度比阅读量重要。",
  },
  {
    index: "03",
    title: "只面向成年人",
    text: "年龄确认不是一道形式上的门。未成年人不应接触本站任何内容，这是没有讨论空间的底线。",
  },
];

const team = [
  { name: "顾青", role: "影视主编", desc: "十年影评写作，关注类型片与心理惊悚题材。" },
  { name: "陈默", role: "游戏编辑", desc: "长期跟踪叙事游戏与独立团队，负责版本更新与访谈。" },
  { name: "林知", role: "漫画编辑", desc: "漫画分镜与出版方向，负责单行本条目与作者访谈。" },
  { name: "沈屿", role: "行业观察", desc: "关注分级制度、平台规则与内容合规的实践变化。" },
];

export default function AboutPage() {
  useDocumentMeta({
    title: "关于我们 - 18禁成人内容资讯平台",
    description:
      "了解18禁网站：一个面向成年观众的成人内容资讯与资源整理平台，介绍团队构成、服务范围、编辑准则与内容免责声明。",
    keywords: "关于18禁,成人内容平台介绍,18禁团队,内容声明,编辑准则",
  });

  return (
    <div>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
          <nav className="mb-5 flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="cursor-pointer transition hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-sm text-foreground-300" aria-hidden="true"></i>
            <span className="text-foreground-700">关于我们</span>
          </nav>
          <h1 className="font-heading text-2xl leading-snug text-foreground-950 md:text-[34px]">关于 18禁</h1>
          <p className="mt-4 max-w-2xl text-sm leading-[1.95] text-foreground-600">
            一个由编辑团队维护的成人内容资讯与资源整理平台。我们相信，成年人有权利获得清晰、准确、不夸张的信息。
          </p>
        </div>
      </div>

      <section className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <div className="mb-3 flex items-center gap-2">
              <span className="h-px w-6 bg-accent-500"></span>
              <span className="text-[11px] font-medium tracking-[0.18em] text-accent-700">OUR POSITION</span>
            </div>
            <h2 className="font-heading text-2xl leading-snug text-foreground-950 md:text-[30px]">网站定位</h2>
            <p className="mt-5 text-sm leading-[1.95] text-foreground-700">
              成人内容长期处在一个信息不对称的环境里：宣传语夸张、分类混乱、缺少基本的适看提示。18禁想做的事情很朴素——把作品信息整理清楚，把内容边界说明白。
            </p>
            <p className="mt-4 text-sm leading-[1.95] text-foreground-700">
              我们不生产内容，也不提供下载。站内呈现的是资讯、条目与讨论，所有作品、评分、人物与数据均为虚构演示，用于展示产品结构与阅读体验。
            </p>
            <div className="mt-8 grid grid-cols-2 gap-3">
              {[
                { label: "内容方向", value: "影视 / 游戏 / 漫画" },
                { label: "目标读者", value: "18 岁以上成年人" },
                { label: "更新节奏", value: "每日资讯 · 每周专题" },
                { label: "内容性质", value: "虚构演示数据" },
              ].map((item) => (
                <div key={item.label} className="rounded-lg border border-background-200 bg-background-100/70 px-4 py-3.5">
                  <p className="text-[10px] tracking-[0.16em] text-foreground-500">{item.label}</p>
                  <p className="mt-1.5 text-sm font-medium text-foreground-900">{item.value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="h-[280px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[400px]">
              <img
                src="https://readdy.ai/api/search-image?query=quiet%20editorial%20reading%20room%20with%20stacked%20books%2C%20warm%20lamp%20light%2C%20deep%20burgundy%20and%20amber%20tones%2C%20no%20people%2C%20refined%20interior%20photography%2C%20soft%20shadows&width=1200&height=800&seq=about-room-01&orientation=landscape"
                alt="18禁编辑部阅读室示意"
                title="18禁编辑团队工作场景示意"
                className="h-full w-full object-top object-cover"
              />
            </div>
            <div className="absolute -bottom-4 left-4 right-4 rounded-md border border-background-200 bg-background-50/95 px-4 py-3 backdrop-blur md:left-6 md:right-auto md:w-[280px]">
              <p className="text-[10px] tracking-[0.18em] text-foreground-400">EDITORIAL DESK</p>
              <p className="mt-1 text-xs leading-relaxed text-foreground-700">
                「先把信息说清楚，再谈推荐。」
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-16">
          <div className="mb-8 max-w-2xl">
            <div className="mb-3 flex items-center gap-2">
              <span className="h-px w-6 bg-accent-500"></span>
              <span className="text-[11px] font-medium tracking-[0.18em] text-accent-700">WHAT WE DO</span>
            </div>
            <h2 className="font-heading text-2xl text-foreground-950 md:text-[30px]">服务范围</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              三条主线构成站点的主要内容，互相之间通过标签与分类打通。
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-3 md:gap-5">
            {services.map((service) => (
              <div key={service.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className={`${service.icon} text-xl`} aria-hidden="true"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950">{service.title}</h3>
                <p className="mt-2.5 text-[13px] leading-[1.9] text-foreground-600">{service.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-16">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.4fr] lg:gap-16">
          <div>
            <div className="mb-3 flex items-center gap-2">
              <span className="h-px w-6 bg-accent-500"></span>
              <span className="text-[11px] font-medium tracking-[0.18em] text-accent-700">OUR PRINCIPLES</span>
            </div>
            <h2 className="font-heading text-2xl text-foreground-950 md:text-[30px]">编辑准则</h2>
            <p className="mt-4 text-sm leading-[1.95] text-foreground-600">
              这三条准则决定了我们怎么写标题、怎么排序、以及哪些内容不会出现在首页。
            </p>
          </div>
          <div className="space-y-4">
            {principles.map((item) => (
              <div key={item.index} className="flex gap-5 rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="font-heading text-lg text-accent-600">{item.index}</span>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-[13px] leading-[1.9] text-foreground-600">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-16">
          <div className="mb-8 max-w-2xl">
            <div className="mb-3 flex items-center gap-2">
              <span className="h-px w-6 bg-accent-500"></span>
              <span className="text-[11px] font-medium tracking-[0.18em] text-accent-700">THE TEAM</span>
            </div>
            <h2 className="font-heading text-2xl text-foreground-950 md:text-[30px]">团队介绍</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              一个小规模的编辑团队，分工覆盖影视、游戏、漫画与行业观察。以下人物与简介均为虚构演示。
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {team.map((member) => (
              <div key={member.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-primary-600 font-heading text-base text-background-50">
                  {member.name.slice(0, 1)}
                </div>
                <p className="text-sm font-semibold text-foreground-950">{member.name}</p>
                <p className="mt-0.5 text-[11px] tracking-wide text-accent-700">{member.role}</p>
                <p className="mt-3 text-[12px] leading-relaxed text-foreground-600">{member.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-16">
        <div className="grid gap-8 lg:grid-cols-2 lg:gap-16">
          <div>
            <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">业务范围与免责声明</h2>
            <div className="mt-5 space-y-4 text-[13px] leading-[1.95] text-foreground-600">
              <p>
                本站提供成人内容相关的资讯整理、作品条目介绍与话题讨论服务，不涉及任何内容的制作、发行、销售或传播。
              </p>
              <p>
                站内所有作品名称、人物、评分、讨论内容、图片来源与统计数据均为虚构演示，仅用于展示产品结构与页面效果，与任何真实作品或真实人物无关。
              </p>
              <p>
                本站不提供资源下载、在线播放、网盘链接或任何形式的外部跳转。若你在其他渠道看到以本站名义提供的资源，请勿相信。
              </p>
              <p>
                本站内容仅面向 18 岁以上成年人。未成年人请立即关闭页面。读者在浏览过程中应遵守所在地法律法规。
              </p>
            </div>
          </div>
          <div className="rounded-lg border border-primary-200 bg-primary-50 p-6 md:p-8">
            <div className="flex items-center gap-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-sm bg-primary-600 text-[11px] font-bold text-background-50">
                18+
              </span>
              <p className="text-sm font-semibold text-primary-800">访问须知</p>
            </div>
            <ul className="mt-4 space-y-3 text-[12px] leading-relaxed text-primary-800/90">
              <li className="flex gap-2">
                <i className="ri-check-line mt-0.5 text-sm text-primary-600" aria-hidden="true"></i>
                进入本站前需确认已年满 18 岁，确认结果仅保存在本地浏览器。
              </li>
              <li className="flex gap-2">
                <i className="ri-check-line mt-0.5 text-sm text-primary-600" aria-hidden="true"></i>
                收藏功能使用浏览器本地存储，清除浏览器数据后收藏记录会一并清除。
              </li>
              <li className="flex gap-2">
                <i className="ri-check-line mt-0.5 text-sm text-primary-600" aria-hidden="true"></i>
                联系表单仅用于收集内容建议，请勿提交任何个人敏感信息。
              </li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-xs font-medium text-background-50 transition hover:bg-primary-700"
              >
                联系编辑部
              </Link>
              <Link
                to="/topics"
                className="cursor-pointer whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-5 py-2.5 text-xs font-medium text-primary-700 transition hover:border-primary-400"
              >
                前往话题区
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}